import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, CheckCircle2, AlertOctagon, CalendarClock, ShieldCheck } from 'lucide-react';
import './PlannedMaintenance.css';

const PlannedMaintenance = () => {
    useEffect(() => {
        document.title = "Planned Maintenance & Proactive Asset Care | X-Max";
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute('content', 'Shift from reactive repairs to proactive asset care with X-Max Planned Maintenance. Digitise schedules, automate tasks, and extend equipment life.');
        }
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="pm-page">
            {/* Hero Section */}
            <section className="pm-hero">
                <div className="container pm-hero-container">
                    <div className="pm-hero-content">
                        <div className="sec-tag">CORE MODULES</div>
                        <h1>Planned Maintenance: <span className="gradient-text">Proactive Asset Care</span></h1>
                        <p className="hero-desc">
                            X-Max's Planned Maintenance module drives a critical shift from reactive repairs to a strategic, proactive approach, ensuring optimal asset health and uninterrupted operational flow.
                        </p>
                        <p className="hero-subtext">
                            By digitising maintenance schedules and checklists, X-Max empowers your teams to <span className="highlight-text">anticipate issues, minimise disruptions</span>, and significantly extend the lifespan of critical machinery.
                        </p>
                        <Link to="/book-now" className="btn btn-primary btn-lg">
                            Book Demo <ChevronRight size={20} />
                        </Link>
                    </div>
                    <div className="pm-hero-visual">
                        <div className="visual-wrapper">
                            <img
                                src="./assets/modules/oeeone.png"
                                alt="Advanced Asset Maintenance"
                                className="hero-img"
                            />
                            <div className="visual-overlay"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Highlights Section */}
            <section className="pm-highlights">
                <div className="container">
                    <div className="highlights-grid">
                        {/* Point 1 */}
                        <div className="highlight-card">
                            <div className="card-step">01</div>
                            <div className="card-icon-box accent-red">
                                <AlertOctagon size={32} />
                            </div>
                            <div className="card-content">
                                <h3>Reactive Breakdowns</h3>
                                <p>Minimise unscheduled downtime, high repair costs, and unpredictable operations.</p>
                            </div>
                        </div>

                        {/* Point 2 */}
                        <div className="highlight-card">
                            <div className="card-step">02</div>
                            <div className="card-icon-box accent-orange">
                                <CalendarClock size={32} />
                            </div>
                            <div className="card-content">
                                <h3>Scheduled PM & Checklists</h3>
                                <p>Digitise planning, automate tasks, and standardise maintenance procedures.</p>
                            </div>
                        </div>

                        {/* Point 3 */}
                        <div className="highlight-card">
                            <div className="card-step">03</div>
                            <div className="card-icon-box accent-emerald">
                                <ShieldCheck size={32} />
                            </div>
                            <div className="card-content">
                                <h3>Enhanced Asset Reliability</h3>
                                <p>Achieve extended equipment life, reduced emergency interventions, and consistent production.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="pm-details">
                <div className="container">
                    <div className="details-layout">
                        <div className="details-text">
                            <p>
                                Shift from manual tracking to a high-performance **Digital PM System**. Our module provides a centralized cockpit to manage your entire asset library, ensuring that every piece of machinery is serviced at precise intervals.
                            </p>
                            <p>
                                By integrating with live production data, X-Max can trigger maintenance alerts based on actual machine usage rather than just calendar days, providing a truly usage-based maintenance strategy.
                            </p>
                        </div>
                        <div className="details-features">
                            <h2>Key Features</h2>
                            <ul className="feature-list">
                                <li>
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>Centralised asset health register</span>
                                </li>
                                <li>
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>Digital PM scheduling & job tracking</span>
                                </li>
                                <li>
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>Interactive spare parts inventory</span>
                                </li>
                                <li>
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>Mean Time Between Failure (MTBF) analytics</span>
                                </li>
                                <li>
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>Automated notification & escalation</span>
                                </li>
                            </ul>
                            <div className="details-cta">
                                <button className="link-btn">
                                    View Asset Management Flow <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="pm-footer-cta">
                <div className="container">
                    <div className="cta-card">
                        <h2>Eliminate unscheduled breakdowns today.</h2>
                        <Link to="/book-now" className="btn btn-primary btn-lg">Schedule Maintenance Audit</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PlannedMaintenance;
